import { Badge } from 'lib/components/Badge';
import { CardGridLayout, GridInput } from 'lib/components/Card/Grid';
import { LoadingSpinner } from 'lib/components/LoadingSpinner';
import { useAppParams } from 'lib/frontend/utils/router';
import { SyncAttemptStatusMap } from 'lib/frontend/utils/syncAttemptStatus';
import { SyncAttemptModel } from 'lib/model/objects/syncAttemptModel';
import { SyncAttemptStatus, TriggerEventMap } from 'lib/types/syncAttempt';
import { CLASSIFIED_ROUTES } from 'router/routes';
import { getFirebaseContext } from 'utils/firebase';
import { useSyncAttempts } from './useSyncAttempts';

export function SyncAttemptStatusBadge({
  status
}: {
  status: SyncAttemptStatus;
}) {
  const { badgeStatus, Icon, label } = SyncAttemptStatusMap[status];
  return (
    <Badge
      status={badgeStatus}
      startIcon={<Icon className="w-4 h-4" />}
      size="md"
    >
      {label}
    </Badge>
  );
}

export function SyncAttemptTimestamp({
  syncAttempt
}: {
  syncAttempt: SyncAttemptModel;
}) {
  const timestamp =
    syncAttempt.modelData.status === SyncAttemptStatus.IN_PROGRESS
      ? syncAttempt.modelData.createdAt
      : syncAttempt.modelData.finalizedAt;
  return <span>{timestamp?.toDate().toLocaleString()}</span>;
}

function SyncAttemptIssue({
  reason,
  toFix
}: {
  reason: string;
  toFix?: string;
}) {
  return (
    <li>
      {reason}.{' '}
      {toFix && (
        <>
          <span className="font-semibold">To Fix:</span> {toFix}
        </>
      )}
    </li>
  );
}

function SyncAttemptDetails({
  syncAttempt
}: {
  syncAttempt: SyncAttemptModel;
}) {
  const { status } = syncAttempt.modelData;

  if (status === SyncAttemptStatus.IN_PROGRESS) {
    return <span>Sync attempt in progress</span>;
  }

  switch (status) {
    case SyncAttemptStatus.SUCCESS: {
      const { message } = syncAttempt.modelData;
      return <>{message}</>;
    }
    default: {
      const { issues } = syncAttempt.modelData;
      return (
        <div className="flex flex-col gap-1">
          <span className="font-semibold">Issues:</span>
          <ul className="list-disc pl-4">
            {issues.map((issue, idx) => (
              <SyncAttemptIssue
                key={`${syncAttempt.id}-issue-${idx}`}
                {...issue}
              />
            ))}
          </ul>
        </div>
      );
    }
  }
}

export function SyncAttemptsTab() {
  const { id: orderId } = useAppParams(CLASSIFIED_ROUTES.DETAIL);
  const orderRef = getFirebaseContext().ordersRef().doc(orderId);

  const syncAttempts = useSyncAttempts(orderRef);

  return (
    <div className="flex flex-col p-6 overflow-y-auto">
      {syncAttempts.length === 0 && <LoadingSpinner />}
      {syncAttempts.map(({ syncAttempt, triggerEvent }) => (
        <CardGridLayout
          key={syncAttempt.id}
          header={{
            title: (
              <div className="flex justify-between">
                {TriggerEventMap[triggerEvent.modelData.type]}
                <SyncAttemptStatusBadge status={syncAttempt.modelData.status} />
              </div>
            ),
            description: <SyncAttemptTimestamp syncAttempt={syncAttempt} />
          }}
        >
          <GridInput fullWidth>
            <div className="-mt-6">
              <SyncAttemptDetails syncAttempt={syncAttempt} />
            </div>
          </GridInput>
        </CardGridLayout>
      ))}
    </div>
  );
}
